import React, { useCallback, useEffect, useRef, useState } from "react";

import { CircularProgress, Stack } from "@mui/material";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { useDealActions } from "store";
import { IDataExtractionTag, Polygons } from "store/types";

import { DataExtractionPDFReader } from "./DataExtractionPDFReader";

interface Props {
  fileId: number;
  fileUrl: string | null;
  fileName: string;
  extractionTags: IDataExtractionTag[];
  onTagCreated: (tag: IDataExtractionTag, isNew: boolean) => void;
  containerWidth: number;
  readOnly?: boolean;
  showPolygons?: boolean;
  hoveredPolygonId: string | null;
  onHoverPolygon: (polygonId: string | null) => void;
}

const POLL_INTERVAL = 3000;
const MAX_POLL_TIME = 120000;

export const DataExtractionPdfViewer: React.FC<Props> = ({
  fileId,
  fileUrl,
  fileName,
  extractionTags,
  onTagCreated,
  containerWidth,
  readOnly,
  showPolygons,
  hoveredPolygonId,
  onHoverPolygon
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [polygons, setPolygons] = useState<Polygons | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval>>();

  const { getFilePolygonsAction, createFileTagAction, updateFileTagAction } = useDealActions();
  const { action: fetchPolygons, loading: polygonsLoading } = useActionHook(getFilePolygonsAction);
  const { action: createTag } = useActionHook(createFileTagAction);
  const { action: updateTag } = useActionHook(updateFileTagAction);

  useEffect(() => {
    if (!fileUrl) return;
    let cancelled = false;
    setLoading(true);
    setFile(null);
    fetch(fileUrl)
      .then(async (response) => {
        if (!response.ok) throw new Error(`Failed to fetch file: ${response.status}`);
        const blob = await response.blob();
        if (!cancelled) {
          setFile(new File([blob], fileName, { type: blob.type }));
        }
      })
      .catch((err) => {
        if (!cancelled) console.error("DataExtractionPdfViewer: file fetch error", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fileUrl, fileName]);

  const handleSelectParameter = useCallback(
    async (data: { measureId: number; filePolygonId: string; value: number; tagId?: number }) => {
      if (data.tagId) {
        const tag = await updateTag(fileId, data.tagId, { measure_id: data.measureId, value: data.value });
        onTagCreated(tag, false);
      } else {
        const tag = await createTag(fileId, { measure_id: data.measureId, value: data.value, polygon_id: data.filePolygonId });
        onTagCreated(tag, true);
      }
    },
    [fileId, createTag, updateTag, onTagCreated]
  );

  useEffect(() => {
    setPolygons(null);
    if (!fileId || readOnly) return;
    let cancelled = false;

    const startTime = Date.now();

    const poll = async () => {
      try {
        const data = await fetchPolygons(fileId);
        if (cancelled) return;
        const hasPages = data && Object.keys(data).length > 0;
        if (hasPages) {
          setPolygons(data);
          clearInterval(pollingRef.current);
        } else if (Date.now() - startTime > MAX_POLL_TIME) {
          clearInterval(pollingRef.current);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("DataExtractionPdfViewer: polygon polling error", err);
        if (Date.now() - startTime > MAX_POLL_TIME) {
          clearInterval(pollingRef.current);
        }
      }
    };

    poll();
    pollingRef.current = setInterval(poll, POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(pollingRef.current);
    };
  }, [fileId, readOnly]);

  if (loading) {
    return (
      <Stack sx={{ flex: 1, minHeight: 0, alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!file) {
    return (
      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          alignItems: "center",
          justifyContent: "center",
          color: "#496682",
          fontSize: "14px"
        }}
      >
        No file available
      </Stack>
    );
  }

  return (
    <DataExtractionPDFReader
      file={file}
      polygons={readOnly ? null : polygons}
      polygonsLoading={readOnly ? false : polygonsLoading}
      extractionTags={extractionTags}
      onSelectParameter={handleSelectParameter}
      containerWidth={containerWidth}
      showPolygons={showPolygons}
      hoveredPolygonId={hoveredPolygonId}
      onHoverPolygon={onHoverPolygon}
    />
  );
};
